import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["header", "headerActions", "description", "error", "isLoading", "content", "appearsIn", "navMarker", "errorMarker", "showNext", "showPrevious", "onClickNext", "onClickPrevious", "onItemClick", "onItemMenuClick", "use", "showDescription", "setMaxCards", "minCardSize", "showOverview", "size", "direction", "maxSkeletonListLength", "flush", "cardsContainerProps"];
import ApiError from '../../../components/Errors/ApiError';
import { PageSectionHeading } from '../../../components/SectionHeading';
import styled, { useMediaQuery } from 'academy-common-lib/utils/Styling';
import I18n from 'I18n';
import { ConditionalNavMarker } from '../../rhumb';
import { FULL_CARD_SIZES } from '../../cards/cardsTypes';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import debounce from 'hs-lodash/debounce';
import UsageTracker from '../../../utils/UsageTracker';
import { LearningContentCard } from '../../cards/components/LearningContentCard';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { Text } from 'foundations-components/components/Text';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LearningContentCardsSkeleton } from '../../../components/Skeletons/LearningContentCardsSkeleton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_MIN_CARD_SIZE = {
  'extra-large': 320,
  large: 250,
  medium: 380,
  small: 340
};
const ResponsiveRow = styled.div.withConfig({
  displayName: "CardsSection__ResponsiveRow"
})(["display:", ";flex-direction:column;width:100%;grid-template-columns:repeat( auto-fill,minmax( ", "px,1fr ) );grid-template-rows:1fr;", ";grid-auto-rows:0;"], ({
  direction
}) => direction === 'column' ? 'flex' : 'grid', ({
  minCardSize,
  $size
}) => minCardSize || DEFAULT_MIN_CARD_SIZE[$size], ({
  direction,
  isMobile
}) => direction === 'column' ? `row-gap: ${isMobile ? '8px' : '20px'}` : `grid-column-gap: ${isMobile ? '8px' : '24px'}`);
export const useSetMaxCards = ({
  renderArea,
  setMaxCards
}) => {
  const debouncedUpdate = useMemo(() => debounce(() => {
    if (!setMaxCards || !renderArea.current) return;
    const cardsThatFitOnAreaCount = window.getComputedStyle(renderArea.current).getPropertyValue('grid-template-columns').split(' ').length;
    setMaxCards(cardsThatFitOnAreaCount);
  }, 200), [renderArea, setMaxCards]);
  useLayoutEffect(() => {
    if (!setMaxCards || !renderArea.current) return;
    debouncedUpdate();
    window.addEventListener('resize', debouncedUpdate);
    return () => window.removeEventListener('resize', debouncedUpdate);
  }, [debouncedUpdate, setMaxCards, renderArea]);
};
export const CardsSection = _ref => {
  let {
      header,
      headerActions,
      description,
      error,
      isLoading: isLoadingProp,
      content,
      appearsIn,
      navMarker,
      errorMarker,
      showNext: showNextParam,
      showPrevious: showPreviousParam,
      onClickNext,
      onClickPrevious,
      onItemClick,
      onItemMenuClick,
      use,
      showDescription,
      setMaxCards,
      minCardSize,
      showOverview,
      size = 'large',
      direction,
      maxSkeletonListLength,
      flush,
      cardsContainerProps
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isTablet,
    isMobile
  } = useMediaQuery();
  const cardsSectionRef = useRef(null);
  useSetMaxCards({
    renderArea: cardsSectionRef,
    setMaxCards
  });
  const handleOnNextClick = useCallback(() => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'card-section-next-click',
      appearsIn
    });
    if (onClickNext) onClickNext();
  }, [appearsIn, onClickNext]);
  const handleOnPreviousClick = useCallback(() => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'card-section-previous-click',
      appearsIn
    });
    if (onClickPrevious) onClickPrevious();
  }, [appearsIn, onClickPrevious]);
  const handleOnItemClick = useCallback((item, index) => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'card-section-item-click',
      id: item.id,
      language: item.language,
      type: item.type,
      appearsIn,
      name: item.name,
      cardNumber: index
    });
    if (onItemClick) onItemClick(item, index);
  }, [appearsIn, onItemClick]);
  const rows = useMemo(() => content && content.map((item, i) => /*#__PURE__*/_jsx(LearningContentCard, {
    body: !showDescription ? undefined : null,
    flush: flush,
    item: item,
    onItemClick: clickedItem => handleOnItemClick(clickedItem, i),
    onItemMenuClick: clickedItem => onItemMenuClick === null || onItemMenuClick === void 0 ? void 0 : onItemMenuClick(clickedItem, i),
    showOverview: showOverview,
    size: size,
    use: use
  }, `${item.id}`)), [content, flush, handleOnItemClick, onItemMenuClick, showDescription, showOverview, size, use]);
  if (error) return /*#__PURE__*/_jsx(ConditionalNavMarker, {
    name: errorMarker,
    children: /*#__PURE__*/_jsx(ApiError, {
      error: error
    })
  });
  const isLoading = isLoadingProp || !rows;
  if (!isLoading && !rows.length) return null; // if empty results don't render the component

  const showNext = !isLoading && showNextParam && (!FULL_CARD_SIZES.includes(size) || !isTablet);
  const showPrevious = !isLoading && showPreviousParam && (!FULL_CARD_SIZES.includes(size) || !isTablet);
  return /*#__PURE__*/_jsxs("div", Object.assign({}, props, {
    "data-loading": isLoading,
    style: {
      width: '100%'
    },
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: isTablet ? 'start' : 'center',
      flexDirection: isTablet ? 'column' : 'row',
      marginBlockEnd: isTablet ? '400' : '0',
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 1,
        children: header && /*#__PURE__*/_jsx(PageSectionHeading, {
          subtitle: description,
          title: header
        })
      }), /*#__PURE__*/_jsx(Text, {
        textWrap: "nowrap",
        children: headerActions
      })]
    }), /*#__PURE__*/_jsx(ConditionalNavMarker, {
      name: navMarker,
      children: /*#__PURE__*/_jsxs(Box, Object.assign({
        paddingBlockEnd: "400",
        position: "relative"
      }, cardsContainerProps, {
        children: [/*#__PURE__*/_jsx(ResponsiveRow, {
          $size: size,
          direction: direction,
          isMobile: isMobile,
          minCardSize: minCardSize,
          ref: cardsSectionRef,
          style: {
            overflow: isLoading ? 'hidden' : undefined
          },
          children: isLoading ? /*#__PURE__*/_jsx(LearningContentCardsSkeleton, {
            maxSkeletonListLength: maxSkeletonListLength,
            skeletonCardSize: size,
            use: use || 'on-light'
          }) : rows
        }), showPrevious && /*#__PURE__*/_jsx(Box, {
          alignItems: "center",
          display: "flex",
          insetBlockStart: "50%",
          insetInlineStart: "0",
          position: "absolute",
          children: /*#__PURE__*/_jsx("div", {
            style: {
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            },
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": I18n.text('learningCenter.common.loadPreviousButton'),
              onClick: handleOnPreviousClick,
              shape: "circle",
              children: /*#__PURE__*/_jsx(LeftIcon, {})
            })
          })
        }), showNext && /*#__PURE__*/_jsx(Box, {
          alignItems: "center",
          display: "flex",
          insetBlockStart: "50%",
          insetInlineEnd: "0",
          position: "absolute",
          children: /*#__PURE__*/_jsx("div", {
            style: {
              transform: 'translate(50%, -50%)',
              zIndex: 10
            },
            children: /*#__PURE__*/_jsx(IconButton, {
              "aria-label": I18n.text('learningCenter.common.loadNextButton'),
              onClick: handleOnNextClick,
              shape: "circle",
              children: /*#__PURE__*/_jsx(RightIcon, {})
            })
          })
        })]
      }))
    })]
  }));
};
export default CardsSection;